import classNames from 'classnames';
import { BaseInput, BaseInputProps, FormRegisterType } from 'components/1-atoms/BaseInput';
import { Button } from 'components/1-atoms/Buttons';
import { ErrorMessage } from 'components/1-atoms/ErrorMessage';
import { Icon, IconProps } from 'components/1-atoms/Media';
import Calculator from 'icons/calculator.svg';
import Calendar from 'icons/icon-calendar.svg';
import Search from 'icons/search.svg';
import Settings from 'icons/settings.svg';
import * as React from 'react';
import styles from './Input.module.scss';

export type ButtonIcon = 'beregn' | 'indstillinger' | 'soeg' | 'kalender' | 'kroner';

type InputButtonType = {
	icon?: ButtonIcon;
	decoratorText?: string;
	ariaLabel: string;
	append?: boolean;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	type: UI.ButtonType;
};

export interface InputProps extends BaseInputProps {
	className?: string;
	appendText?: string;
	shortAppendText?: boolean;
	button?: InputButtonType;
	disabled?: boolean;
	defaultValue?: string | number;
	value?: number | string;
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	errors?: string[];
	errorIcon?: IconProps;
	formRegister?: FormRegisterType;
}

export const Input: React.FC<InputProps> = ({
	className,
	appendText,
	shortAppendText,
	button,
	disabled,
	defaultValue,
	value,
	onChange,
	errors,
	errorIcon,
	formRegister,
	...inputProps
}: InputProps) => {
	const getButtonIcon = (buttonIcon: ButtonIcon) => {
		switch (buttonIcon) {
			case 'beregn':
				return <Calculator />;
			case 'indstillinger':
				return <Settings />;
			case 'soeg':
				return <Search />;
			case 'kalender':
				return <Calendar />;
			default:
				return null;
		}
	};

	return (
		<>
			<div className={classNames(styles.Input, className)}>
				<div className={classNames(styles.Input_container, (button?.icon || appendText) && styles.Input_container___withButtonOrAppendtext)}>
					{button && (
						<div
							className={classNames(styles.Input_extend, button.append ? styles.Input_extend___append : styles.Input_extend___prepend)}
						>
							{button?.icon && (
								<Button ariaLabel={button.ariaLabel} onClick={button.onClick} type={button.type} buttonSize="small">
									<Icon className={styles.Input_extend___icon} size={button.icon === 'kalender' ? 'md' : 'sm'}>
										{getButtonIcon(button.icon)}
									</Icon>
								</Button>
							)}
						</div>
					)}
					<BaseInput
						className={classNames(styles.Input_input, styles.Input_input___withButtonIcon)}
						disabled={disabled}
						hasErrors={errors && errors.length > 0}
						onChange={onChange}
						value={value}
						defaultValue={defaultValue}
						formRegister={formRegister}
						{...inputProps}
					/>
					{appendText && (
						<div className={classNames(styles.Input_extend, styles.Input_extend___append)}>
							<span
								className={classNames(
									styles.Input_extend___text,
									shortAppendText && styles.Input_extend___shortAppendText,
									disabled ? styles.Input_extend___disabled : '',
									errors && errors.length > 0 && styles.Input_extend___hasError,
								)}
							>
								{appendText}
							</span>
						</div>
					)}
				</div>

				<div>
					{errors &&
						errors.length > 0 &&
						errors.map((error, index) => (
							<div key={error + index} className={styles.Input_errorMessage}>
								{errorIcon && <Icon className={styles.Input_helpButton} {...errorIcon} />}
								<ErrorMessage className={styles.Input_errorText}>{error}</ErrorMessage>
							</div>
						))}
				</div>
			</div>
		</>
	);
};
